import { useState, useEffect, useRef, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Bell, User, ArrowLeft } from 'lucide-react'
import { NotificationDropdown } from '../NotificationDropdown'
import { AccountDropdown } from '../AccountDropdown'
import { useAuth } from '../../hooks/useAuth'

export interface HeaderContextChip {
  icon?: ReactNode
  /** Primary line — truncated with ellipsis if too long. */
  title: string
  /** Optional secondary line, shown smaller below the title. */
  subtitle?: string
}

interface HeaderProps {
  variant?: 'home' | 'campaigns'
  /** Optional Claude-style context chip rendered next to the logo. */
  contextChip?: HeaderContextChip | null
}

export function Header({ variant = 'home', contextChip = null }: HeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, admin } = useAuth()

  // Resolve the back destination: prefer the explicit origin passed via
  // location.state.from (set when navigating into the campaign details page
  // from /campaigns(?...) or /c/:conversationId). Fall back to /campaigns.
  const handleBack = () => {
    const fromState = (location.state as { from?: string } | null)?.from
    const isAllowedFrom =
      typeof fromState === 'string' &&
      (fromState === '/campaigns' ||
        fromState.startsWith('/campaigns?') ||
        fromState.startsWith('/c/'))
    navigate(isAllowedFrom ? fromState! : '/campaigns')
  }
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false)
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false)

  const notificationDropdownRef = useRef<HTMLDivElement>(null)
  const accountDropdownRef = useRef<HTMLDivElement>(null)

  const userInfo = user || admin
  const initials = userInfo?.full_name
    ? userInfo.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (userInfo?.email?.[0] || 'U').toUpperCase()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target as Node)) {
        setIsNotificationDropdownOpen(false)
      }
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setIsAccountDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header className="fixed top-0 w-full z-[100] bg-white/95 dark:bg-slate-900/80 backdrop-blur border-b border-slate-200/70 dark:border-slate-800/80 h-14 sm:h-16">
      <div className="w-full px-2 sm:px-3 lg:px-4">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Left Side */}
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            {variant === 'campaigns' && (
              <button
                onClick={handleBack}
                className="flex-shrink-0 p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800/70 rounded-lg transition-colors"
                title="Go Back"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            {/* The new AgenX wordmark already includes the brand text, so
                we drop the adjacent <h1>. We render a black/white pair and
                toggle them with `dark:` utilities so the logo always has
                AA contrast against the surface.

                On the home variant the page renders a fixed `w-64`
                conversation sidebar starting at the left edge. We mirror
                that width on the logo wrapper and center the wordmark
                inside it so the logo sits visually above the sidebar's
                horizontal center instead of hugging the left edge. Other
                variants keep the original left-aligned layout. */}
            <div
              className={`flex items-center flex-shrink-0 ${
                variant === 'home'
                  ? // Sidebar is `w-64` and starts at the page's left edge,
                    // but the header has `px-2 sm:px-3 lg:px-4` padding so a
                    // pure `w-64 justify-center` lands slightly right of the
                    // sidebar's true center. We shrink the wrapper a touch
                    // (`w-60`) and nudge it left with a small negative margin
                    // so the wordmark sits visually centered above the
                    // sidebar column.
                    'w-60 justify-center -ml-1 sm:-ml-2 lg:-ml-3'
                  : ''
              }`}
            >
              <img
                src="/main_logo/agenx_logo_black.svg"
                alt="AgenX"
                className={`block dark:hidden w-auto ${variant === 'home' ? 'h-9 sm:h-10' : 'h-7 sm:h-8'}`}
              />
              <img
                src="/main_logo/agenx_logo_white.svg"
                alt="AgenX"
                aria-hidden="true"
                className={`hidden dark:block w-auto ${variant === 'home' ? 'h-9 sm:h-10' : 'h-7 sm:h-8'}`}
              />
            </div>

            {/* Optional context chip — Claude-style compact pill that sits
                next to the logo and surfaces page-level context (e.g. the
                current campaign objective) with ellipsis truncation. */}
            {contextChip && (
              <div
                title={
                  contextChip.subtitle
                    ? `${contextChip.title} — ${contextChip.subtitle}`
                    : contextChip.title
                }
                className="flex items-center gap-2 min-w-0 max-w-[200px] sm:max-w-[320px] lg:max-w-[440px] pl-2.5 pr-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/60 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-default"
              >
                {contextChip.icon && (
                  <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white dark:bg-slate-900 text-primary-600 dark:text-primary-400 ring-1 ring-slate-200 dark:ring-slate-700">
                    {contextChip.icon}
                  </span>
                )}
                <div className="flex flex-col min-w-0 leading-tight">
                  <span className="text-xs sm:text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {contextChip.title}
                  </span>
                  {contextChip.subtitle && (
                    <span className="text-[10px] sm:text-[11px] text-slate-500 dark:text-slate-400 truncate">
                      {contextChip.subtitle}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center space-x-2">
            {/* Notification Icon */}
            <div className="relative" ref={notificationDropdownRef}>
              <button
                onClick={() => setIsNotificationDropdownOpen(!isNotificationDropdownOpen)}
                className="p-2 text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800/70 rounded-lg transition-colors duration-200 relative"
                title="Notifications"
              >
                <Bell className="h-5 w-5" />
              </button>

              {isNotificationDropdownOpen && (
                <NotificationDropdown
                  isOpen={isNotificationDropdownOpen}
                  onClose={() => setIsNotificationDropdownOpen(false)}
                  isDropdown={true}
                  dropdownRef={notificationDropdownRef}
                />
              )}
            </div>

            {/* Account Icon */}
            <div className="relative" ref={accountDropdownRef}>
              <button
                onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
                className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium hover:bg-primary-700 transition-colors duration-200 border-2 border-slate-200/70 dark:border-slate-800"
                title="Account"
              >
                {variant === 'home' ? (
                  <span>{initials}</span>
                ) : (
                  <User className="h-5 w-5" />
                )}
              </button>

              {isAccountDropdownOpen && (
                <AccountDropdown
                  isOpen={isAccountDropdownOpen}
                  onClose={() => setIsAccountDropdownOpen(false)}
                  isDropdown={true}
                  dropdownRef={accountDropdownRef}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
